import type { CampaignPaymentListStatus } from '@/types/campaign-report'
import { PAYMENT_STATUS_LABELS } from '@/lib/pedidos-filters'

export type { CampaignPaymentListStatus }

export { PAYMENT_STATUS_LABELS }

export const MIN_STATUS_CHANGE_REASON_LENGTH = 10
export const MAX_STATUS_CHANGE_REASON_LENGTH = 500

export function normalizePaymentStatus(status: string | undefined | null): CampaignPaymentListStatus | null {
  if (!status) return null
  const normalized = status.trim().toLowerCase()
  if (normalized in PAYMENT_STATUS_LABELS) {
    return normalized as CampaignPaymentListStatus
  }
  return null
}

export function formatStatusLabel(status: string | undefined | null): string {
  const normalized = normalizePaymentStatus(status)
  if (!normalized) return status?.trim() || '—'
  return PAYMENT_STATUS_LABELS[normalized]
}

export type PaymentStatusBadgeVariant =
  | 'paid'
  | 'pending'
  | 'expired'
  | 'canceled'
  | 'failed'
  | 'unknown'

export function statusBadgeVariant(status: string | undefined | null): PaymentStatusBadgeVariant {
  const normalized = normalizePaymentStatus(status)
  if (!normalized) return 'unknown'
  return normalized
}

const ALL_STATUSES: CampaignPaymentListStatus[] = [
  'pending',
  'paid',
  'expired',
  'canceled',
  'failed',
]

/** Institution owners / platform admins may move to any other status. */
const ALLOWED_STATUS_TRANSITIONS: Record<
  CampaignPaymentListStatus,
  CampaignPaymentListStatus[]
> = {
  pending: ALL_STATUSES.filter((s) => s !== 'pending'),
  expired: ALL_STATUSES.filter((s) => s !== 'expired'),
  failed: ALL_STATUSES.filter((s) => s !== 'failed'),
  paid: ALL_STATUSES.filter((s) => s !== 'paid'),
  canceled: ALL_STATUSES.filter((s) => s !== 'canceled'),
}

export function getAllowedStatusTransitions(
  status: string | undefined | null
): CampaignPaymentListStatus[] {
  const normalized = normalizePaymentStatus(status)
  if (!normalized) return []
  return ALLOWED_STATUS_TRANSITIONS[normalized]
}

export function resolveStatusTransitions(
  status: string | undefined | null,
  fromApi: CampaignPaymentListStatus[] | undefined | null
): CampaignPaymentListStatus[] {
  if (fromApi !== undefined && fromApi !== null) {
    return fromApi
  }
  return getAllowedStatusTransitions(status)
}

export function buildStatusTransitionOptions(
  allowed: CampaignPaymentListStatus[] | undefined
): { value: string; label: string }[] {
  return (allowed ?? []).map((status) => ({
    value: status,
    label: PAYMENT_STATUS_LABELS[status],
  }))
}

export function statusChangeConsequenceHint(
  fromStatus: string | undefined | null,
  toStatus: CampaignPaymentListStatus | null
): string | null {
  if (!toStatus) return null
  const from = normalizePaymentStatus(fromStatus)

  if (from === 'paid' && toStatus !== 'paid') {
    return 'Ao sair de Pago, números da sorte serão liberados e comissões/giros gerados por este pedido serão estornados.'
  }

  if (from !== 'paid' && toStatus === 'paid') {
    return 'Ao confirmar como Pago, o sistema gerará números da sorte (se aplicável) e demais efeitos do pedido.'
  }

  return null
}
